import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { AppNotification } from './types';
import {
  getNotifications,
  getUnreadNotificationCount,
  markNotificationRead,
  markAllNotificationsRead,
} from '../../services/apiService';
import {
  PUSH_MESSAGE_EVENT,
  reconcilePushRegistrationOnStartup,
} from '../../services/push-notification-service';
import { useUser } from '../login/UserContext';

interface NotificationContextType {
  notifications: AppNotification[];
  unreadCount: number;
  loading: boolean;
  /** Fetch latest notifications from server */
  refresh: () => Promise<void>;
  /** Mark one notification as read (optimistic) */
  markRead: (id: number) => Promise<void>;
  /** Mark all notifications as read (optimistic) */
  markAllRead: () => Promise<void>;
  /** Mark rows the user scrolled past as read (optimistic, Instagram-style) */
  markSeen: (ids: number[]) => Promise<void>;
}

export const NotificationContext = createContext<NotificationContextType>({
  notifications: [],
  unreadCount: 0,
  loading: false,
  refresh: async () => {},
  markRead: async () => {},
  markAllRead: async () => {},
  markSeen: async () => {},
});

const BADGE_POLL_INTERVAL_MS = 15_000;
const FULL_REFRESH_INTERVAL_MS = 60_000;

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const badgePollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fullRefreshPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const latestCountRequestRef = useRef(0);
  const latestNotificationsRequestRef = useRef(0);
  // Ids marked read on this client. A poll that left before the PUT landed
  // would otherwise flip a just-seen row back to unread.
  const locallyReadRef = useRef<Set<number>>(new Set());
  const { user } = useUser();

  const isAuthenticated = user.id > 0;

  const refresh = useCallback(async () => {
    if (!isAuthenticated) return;
    const notificationsRequestId = latestNotificationsRequestRef.current + 1;
    const countRequestId = latestCountRequestRef.current + 1;
    latestNotificationsRequestRef.current = notificationsRequestId;
    latestCountRequestRef.current = countRequestId;
    setLoading(true);
    try {
      const [notifs, count] = await Promise.all([
        getNotifications(),
        getUnreadNotificationCount(),
      ]);
      if (notificationsRequestId === latestNotificationsRequestRef.current) {
        const locallyRead = locallyReadRef.current;
        setNotifications(
          locallyRead.size === 0
            ? notifs
            : notifs.map((n) => (!n.read && locallyRead.has(n.id) ? { ...n, read: true } : n)),
        );
      }
      if (countRequestId === latestCountRequestRef.current) {
        setUnreadCount(count);
      }
    } catch (err) {
      console.error('[NotificationContext] refresh error:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const refreshUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;
    const countRequestId = latestCountRequestRef.current + 1;
    latestCountRequestRef.current = countRequestId;
    try {
      const count = await getUnreadNotificationCount();
      if (countRequestId === latestCountRequestRef.current) {
        setUnreadCount(count);
      }
    } catch (err) {
      console.error('[NotificationContext] refreshUnreadCount error:', err);
    }
  }, [isAuthenticated]);

  const markSeen = useCallback(async (ids: number[]) => {
    const fresh = ids.filter((id) => !locallyReadRef.current.has(id));
    if (fresh.length === 0) return;
    fresh.forEach((id) => locallyReadRef.current.add(id));
    const freshSet = new Set(fresh);
    // Optimistic update
    setNotifications((prev) =>
      prev.map((n) => (freshSet.has(n.id) ? { ...n, read: true } : n))
    );
    setUnreadCount((prev) => Math.max(0, prev - fresh.length));
    await Promise.all(fresh.map((id) => markNotificationRead(id)));
  }, []);

  const markRead = useCallback((id: number) => markSeen([id]), [markSeen]);

  const markAllRead = useCallback(async () => {
    // Optimistic update
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
    await markAllNotificationsRead();
  }, []);

  // Initial fetch + polling. Keep notification checks from acting like a
  // background keepalive: hidden tabs refresh when they become active again.
  useEffect(() => {
    if (isAuthenticated) {
      const refreshIfVisible = (fullRefresh = false) => {
        if (!document.hidden) {
          void (fullRefresh ? refresh() : refreshUnreadCount());
        }
      };
      const refreshWhenActive = () => {
        if (!document.hidden) {
          void refresh();
        }
      };

      // Foreground web push: refresh the bell the moment a push lands so
      // the badge doesn't wait out the poll interval. (Push supplements the
      // polling rather than replacing it — polling stays the fallback for
      // browsers without push and for missed messages.)
      const onPushMessage = () => {
        void refresh();
      };

      refreshIfVisible(true);
      // Silent token refresh/reconciliation for browsers that already
      // enabled push. Never prompts for permission.
      void reconcilePushRegistrationOnStartup();
      badgePollRef.current = setInterval(refreshIfVisible, BADGE_POLL_INTERVAL_MS);
      fullRefreshPollRef.current = setInterval(() => refreshIfVisible(true), FULL_REFRESH_INTERVAL_MS);
      document.addEventListener('visibilitychange', refreshWhenActive);
      window.addEventListener('focus', refreshWhenActive);
      window.addEventListener('pageshow', refreshWhenActive);
      window.addEventListener('online', refreshWhenActive);
      window.addEventListener(PUSH_MESSAGE_EVENT, onPushMessage);

      return () => {
        document.removeEventListener('visibilitychange', refreshWhenActive);
        window.removeEventListener('focus', refreshWhenActive);
        window.removeEventListener('pageshow', refreshWhenActive);
        window.removeEventListener('online', refreshWhenActive);
        window.removeEventListener(PUSH_MESSAGE_EVENT, onPushMessage);
        if (badgePollRef.current) {
          clearInterval(badgePollRef.current);
          badgePollRef.current = null;
        }
        if (fullRefreshPollRef.current) {
          clearInterval(fullRefreshPollRef.current);
          fullRefreshPollRef.current = null;
        }
      };
    } else {
      setNotifications([]);
      setUnreadCount(0);
      locallyReadRef.current.clear();
    }
    return () => {
      if (badgePollRef.current) {
        clearInterval(badgePollRef.current);
        badgePollRef.current = null;
      }
      if (fullRefreshPollRef.current) {
        clearInterval(fullRefreshPollRef.current);
        fullRefreshPollRef.current = null;
      }
    };
  }, [isAuthenticated, refresh, refreshUnreadCount]);

  return (
    <NotificationContext.Provider
      value={{ notifications, unreadCount, loading, refresh, markRead, markAllRead, markSeen }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);
